import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Socket } from 'ngx-socket-io';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from '../login/auth.service';
import { UserService } from '../services/user.service';
import {
  Order,
  OrderDelivery,
  OrderLight,
  OrderReadiness,
  OrderPaymentStatus,
  OrderProcurementStatus,
  OrderFulfillmentStatus,
  OrderShipmentStatus,
  OrderSource,
  OrderSupplyStatus,
  ProductInDoc,
  User
} from 'src/typings';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SupportService } from '../support.service';
import { Subscription } from 'rxjs';
import { normalizeDateRangePayload } from 'src/app/shared/utils/date-filter.util';

export interface SearchOrderDto {
  id?: number;
  date_from?: Date;
  date_to?: Date;
  client_id?: number;
  user_id?: number;
  warehouse_id?: number;
  client_car_id?: number;
  is_canceled?: boolean;
  supply_status?: OrderSupplyStatus | OrderSupplyStatus[];
  procurement_status?: OrderProcurementStatus | OrderProcurementStatus[];
  fulfillment_status?: OrderFulfillmentStatus | OrderFulfillmentStatus[];
  payment_status?: OrderPaymentStatus | OrderPaymentStatus[];
  shipment_status?: OrderShipmentStatus | OrderShipmentStatus[];
  source?: OrderSource | OrderSource[];
  unassigned?: boolean;
  search_query?: string;
  lookup_query?: string;
  manager_query?: string;
  limit?: number;
  offset?: number;
}

export interface SearchOrderResponseDto {
  data: OrderLight[];
  total: number;
  limit: number;
  offset: number;
}

export interface CancelOrderDto {
  reason?: string;
}

export interface OrderSupplyAllocation {
  id: number;
  order_product_id: number;
  provider_id: number;
  provider: string;
  provider_warehouse_id?: number | null;
  contract_id?: number | null;
  contract_number?: string | null;
  quantity: number;
  received_quantity: number;
  /** Documentary receipt and physical receipt can differ until reconciliation. */
  physical_unreconciled_quantity?: number;
  physically_received_quantity?: number;
  purchase_price?: number | null;
  expected_at?: string | null;
  expected_at_precision?: 'DATE' | 'DATETIME';
  expected_at_confirmed?: boolean | null;
  status: 'PLANNED' | 'ORDERED' | 'PARTIALLY_RECEIVED' | 'RECEIVED' | 'CANCELLED';
  needs_order?: boolean;
  buy_ids: number[];
  confirmed_at?: string | null;
  confirmed_by_id?: number | null;
}

export interface OrderTransferSource {
  moving_doc_id: number;
  status: string;
  source_warehouse_id: number;
  source_warehouse: string;
  target_warehouse_id: number;
  target_warehouse: string;
  quantity: number;
  received_quantity: number;
}

export interface OrderWarehouseAllocation {
  warehouse_id: number;
  quantity: number;
}

export interface OrderSupplyLine {
  warehouse_id?: number;
  warehouse_name?: string;
  workflow_version?: number;
  placed_quantity?: number;
  ready_quantity?: number;
  unattributed_received_quantity?: number;
  unattributed_reserved_quantity?: number;
  picked?: number;
  fulfilled?: number;
  transfer_pending?: number;
  order_product_id: number;
  product_id: number;
  required: number;
  reserved: number;
  planned: number;
  ordered: number;
  received: number;
  transfer_reserved: number;
  transfer_picking: number;
  transfer_in_transit: number;
  moving_doc_ids: number[];
  excluded_warehouse_ids?: number[];
  transfer_sources?: OrderTransferSource[];
  uncovered: number;
  allocations: OrderSupplyAllocation[];
}

export interface OrderSupplyResponse {
  order_id: number;
  readiness?: OrderReadiness;
  lines: OrderSupplyLine[];
  summary: {
    required: number;
    reserved: number;
    planned: number;
    ordered: number;
    received: number;
    uncovered: number;
  };
}

export interface OrderSupplyReceipt {
  import_doc_id: number;
  quantity: number | null;
  date: string | null;
  document_number: string | null;
  scope: 'ORDER_LINE' | 'PURCHASE_LINE';
}

export interface OrderSupplyEtaHistory {
  id: number;
  buy_id: number;
  previous_expected_at: string | null;
  expected_at: string;
  previous_precision: 'DATE' | 'DATETIME' | null;
  precision: 'DATE' | 'DATETIME';
  confirmed: boolean;
  reason: string;
  created_at: string;
  created_by: string | null;
}

export interface OrderSupplyAllocationDetails extends OrderSupplyAllocation {
  eta_history?: OrderSupplyEtaHistory[];
  confirmed_by: string | null;
  created_at: string | null;
  receipts: OrderSupplyReceipt[];
}

export interface OrderTransferSourceDetails extends OrderTransferSource {
  created_at: string | null;
  picked_at: string | null;
  picked_by: string | null;
  dispatched_at: string | null;
  dispatched_by: string | null;
  accepted_at: string | null;
  accepted_by: string | null;
}

export interface OrderSupplyPlacement {
  id: number;
  cell_id: number;
  cell_code: string;
  quantity: number;
  status: string;
  source_type: string;
  created_at: string | null;
}

export interface OrderSupplyHandling {
  id: number;
  task_id: number;
  status: string;
  quantity: number;
  source_cell_code: string | null;
  destination_cell_code: string | null;
  actor_id: number | null;
  actor_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface OrderSupplyPickTask {
  id: number;
  status: string;
  workflow_version: number;
  quantity: number;
  picked_quantity: number;
  short_quantity: number;
  assigned_to_id: number | null;
  assigned_to: string | null;
  created_at: string | null;
  completed_at: string | null;
}

export interface OrderSupplyPendingReceiving {
  id: number;
  quantity: number;
  pending_quantity: number;
  unreconciled_quantity: number;
  source_deferred: boolean;
  status: string;
  cell_code: string | null;
  created_at: string | null;
}

export interface OrderSupplyLineDetails extends OrderSupplyLine {
  receiving_corrections?: Array<{id:number;created_at:string;actor:string;reason:string;session_id:number;quantity:number}>;
  placed_quantity: number;
  in_hands_quantity: number;
  legacy_picked_quantity: number;
  allocations: OrderSupplyAllocationDetails[];
  transfer_sources: OrderTransferSourceDetails[];
  placements: OrderSupplyPlacement[];
  handlings: OrderSupplyHandling[];
  pick_tasks: OrderSupplyPickTask[];
  pending_receiving: OrderSupplyPendingReceiving[];
  receipts: OrderSupplyReceipt[];
}

export interface OrderSupplyDetailsResponse extends OrderSupplyResponse {
  warehouse_id: number;
  warehouse_name: string;
  is_canceled: boolean;
  workflow_version: number;
  lines: OrderSupplyLineDetails[];
}

export interface CreateOrderDto {
  request_id?: string;
  manager_user_id?: number;
  description?: string;
  client_id?: number | null;
  client_name?: string;
  client_surname?: string;
  client_phone?: string;
  client_car_id?: number | null;
  warehouse_id: number;
  dateExpected?: string;
  delivery?: OrderDelivery;
  products: Array<{
    id?: number;
    product_id: number;
    quan: number;
    price: number;
    provider_id?: number;
    warehouse_allocations?: OrderWarehouseAllocation[];
    warehouse_releases?: OrderWarehouseAllocation[];
    supply_allocations?: Array<{
      provider_id: number;
      contract_id?: number;
      provider_warehouse_id?: number;
      quantity: number;
      purchase_price?: number;
      expected_at?: string;
    }>;
  }>;
}

export interface SaveOrderDto {
  request_id?: string;
  id: number;
  description?: string;
  manager_user_id?: number;
  client_id?: number | null;
  client_car_id?: number | null;
  warehouse_id?: number;
  dateExpected?: string;
  delivery?: OrderDelivery;
  products?: Array<{
    id?: number;
    product_id: number;
    quan: number;
    price: number;
    provider_id?: number;
    warehouse_allocations?: OrderWarehouseAllocation[];
    warehouse_releases?: OrderWarehouseAllocation[];
    supply_allocations?: Array<{
      provider_id: number;
      contract_id?: number;
      provider_warehouse_id?: number;
      quantity: number;
      purchase_price?: number;
      expected_at?: string;
    }>;
  }>;
}

export interface OrderStatusChangedEvent {
  order_id: number;
}

export interface OrderFlowDocumentRef {
  type: 'invoice' | 'payment' | 'shipment' | 'sale';
  id: number;
}

export interface OrderFlowResult {
  order_id: number;
  status: 'success';
  created_documents: OrderFlowDocumentRef[];
  reused_documents: OrderFlowDocumentRef[];
  updated_order_state: unknown;
  warnings: string[];
}

export interface CreateOrderPaymentFlowDto {
  request_id?: string;
  value: number;
  payment_type: string;
  date?: Date;
}

export interface CreateOrderShipmentFlowDto {
  date?: Date;
  warehouse_id?: number;
  products?: { product_id: number; quan: number }[];
  allowDebtLimitExceeded?: boolean;
}

export interface CreateOrderSaleFlowDto {
  date?: Date;
  fiscal?: boolean;
  client_id?: number;
  client_car_id?: number;
  org_id?: number;
  org_bank_account_id?: number;
  warehouse_id?: number;
  manager_user_id?: number;
  passed?: boolean;
  paid?: boolean;
  bank_id?: number;
  allowDebtLimitExceeded?: boolean;
  products?: {
    product_id: number;
    quan: number;
    price: number;
  }[];
}

export interface OrderUnavailableProductsPayload {
  source_invoice_id: number;
  cancel_products: { product_id: number; quan: number }[];
  replacement_products?: { product_id: number; quan: number; price: number }[];
  settlement_mode: 'CREDIT_TRANSFER';
  reason?: string;
}

export interface OrderUnavailableProductsResult {
  cancel_total: number;
  credit_applied_total: number;
  extra_payment_due_total: number;
  refund_due_total: number;
  cancellation_id?: number | null;
  target_invoice_id?: number | null;
  credit_transfer_id?: number | null;
  requires_credit_allocation?: boolean;
  wallet_credited_total?: number;
  wallet_credit_potential?: number;
}

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  user: User | undefined;

  orderGW = this.socket.fromEvent<OrderLight[]>('order_resfresh');

  orderStatusChangedGW = this.socket.fromEvent<OrderStatusChangedEvent>('order_status_changed');

  getSupply(orderId: number) {
    return this.http.get<OrderSupplyResponse>(`${environment.BACKEND}/order/${orderId}/supply`);
  }

  getSupplyDetails(orderId: number) {
    return this.http.get<OrderSupplyDetailsResponse>(
      `${environment.BACKEND}/order/${orderId}/supply-details`
    );
  }

  saveSupplyPlan(
    orderId: number,
    payload: {
      lines: Array<{
        order_product_id: number;
        allocations: Array<{
          provider_id: number;
          quantity: number;
          purchase_price?: number;
          expected_at?: string;
        }>;
      }>;
    }
  ) {
    return this.http.put<OrderSupplyResponse>(
      `${environment.BACKEND}/order/${orderId}/supply-plan`,
      payload
    );
  }

  requestProcurement(orderId: number) {
    return this.http.post<{ buy_ids: number[]; supply: OrderSupplyResponse }>(
      `${environment.BACKEND}/order/${orderId}/procurement/request`, {});
  }

  confirmProcurement(
    orderId: number,
    allocationIds?: number[],
    providerContracts: Array<{ provider_id: number; contract_id: number }> = [],
    operationId = this.uuid()
  ) {
    return this.http.post<{ buy_ids: number[]; supply: OrderSupplyResponse }>(
      `${environment.BACKEND}/order/${orderId}/procurement/confirm`,
      {
        allocation_ids: allocationIds,
        operation_id: operationId,
        provider_contracts: providerContracts
      }
    );
  }

  private uuid() {
    const values = new Uint8Array(16);
    window.crypto.getRandomValues(values);
    values[6] = (values[6] & 0x0f) | 0x40;
    values[8] = (values[8] & 0x3f) | 0x80;
    const hex = Array.from(values, (value) => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
      16,
      20
    )}-${hex.slice(20)}`;
  }

  newOrderGW = this.socket.fromEvent<Order>('new_order');

  updateOrderGW = this.socket.fromEvent<Order>('order_update');

  orderClaimedGW = this.socket.fromEvent<{ order_id: number; user_id: number }>('order_claimed');

  ordersChangedGW = this.socket.fromEvent<{ order_ids: number[] }>('orders_changed');

  socketConnectedGW = this.socket.fromEvent<void>('connect');

  userSubscription: Subscription;

  private readonly orderNotificationUpdates: Subscription;

  private readonly orderClaimNotifications: Subscription;

  private readonly orderSounds = new Map<number, HTMLAudioElement>();

  private readonly orderNotices = new Map<number, {
    snackRef: ReturnType<MatSnackBar['open']>;
    subscription: Subscription;
  }>();

  private readonly notifiedOrderIds = new Set<number>();

  private readonly resolvedOrderIds = new Set<number>();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private socket: Socket,
    private snackBar: MatSnackBar,
    private supportService: SupportService
  ) {
    this.userSubscription = this.userService.user.subscribe((user) => {
      this.user = user;
    });
    this.orderNotificationUpdates = this.updateOrderGW.subscribe((order) => {
      if (order?.user || order?.is_canceled) this.resolveOrderNotification(order.id);
    });
    this.orderClaimNotifications = this.orderClaimedGW.subscribe((event) => {
      if (event?.order_id) this.resolveOrderNotification(event.order_id);
    });
  }

  // eslint-disable-next-line @angular-eslint/use-lifecycle-interface
  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
    this.orderNotificationUpdates.unsubscribe();
    this.orderClaimNotifications.unsubscribe();
    for (const orderId of this.orderSounds.keys()) this.stopOrderSound(orderId);
    for (const notice of this.orderNotices.values()) {
      notice.subscription.unsubscribe();
      notice.snackRef.dismiss();
    }
    this.orderNotices.clear();
    this.notifiedOrderIds.clear();
    this.resolvedOrderIds.clear();
  }

  playSound(orderId?: number) {
    const audio = new Audio();
    if (orderId) {
      this.orderSounds.set(orderId, audio);
      const release = () => {
        if (this.orderSounds.get(orderId) === audio) this.orderSounds.delete(orderId);
      };
      audio.addEventListener('ended', release, { once: true });
      audio.addEventListener('error', release, { once: true });
    }
    audio.src = `${environment.BACKEND}/sound/sound.mp3`;
    audio.load();
    void audio.play().catch(() => {
      // Autoplay can be blocked, or acceptance can abort a still-pending play().
      if (orderId && this.orderSounds.get(orderId) === audio) {
        this.stopOrderSound(orderId);
      }
    });
  }

  async newOrderNotification(order: Order) {
    const orderId = Number(order?.id);
    if (!orderId || this.notifiedOrderIds.has(orderId)) return;
    if (order.is_canceled) {
      this.resolveOrderNotification(orderId);
      return;
    }
    const selectionMatch = String(order.description || '').match(/^Підбір #(\d+)/i);
    if (order.user) {
      if (!selectionMatch || Number(order.user.id) !== Number(this.user?.id)) return;

      this.rememberOrderId(this.notifiedOrderIds, orderId);
      const sum = order.products.reduce((prev, curr) => {
        return prev + curr.price * curr.quan;
      }, 0);
      const snackRef = this.snackBar.open(
        `Клиент оформил заказ №${order.id} из подбора №${
          selectionMatch[1]
        } на сумму ${this.supportService.formatMoney(sum)}`,
        'Открыть заказ',
        { duration: 20000 }
      );
      this.playSound(orderId);
      snackRef.onAction().subscribe(() => {
        this.router.navigate(['/orders/update', order.id]);
      });
      return;
    }

    // A delayed creation event must not revive an order already assigned elsewhere.
    if (this.resolvedOrderIds.has(orderId)) return;
    this.rememberOrderId(this.notifiedOrderIds, orderId);
    const sum = order.products.reduce((prev, curr) => {
      return prev + curr.price * curr.quan;
    }, 0);
    const snackRef = this.snackBar.open(
      `Новый заказ на сумму ${this.supportService.formatMoney(sum)}`,
      `Взять в обработку`
    );
    this.playSound(orderId);
    const subscription = new Subscription();
    this.orderNotices.set(orderId, { snackRef, subscription });
    subscription.add(snackRef.onAction().subscribe(() => {
      void this.updateManagerUser(orderId).catch((err) => {
        console.error(err);
      });
    }));
    subscription.add(snackRef.afterDismissed().subscribe(() => {
      if (this.orderNotices.get(orderId)?.snackRef === snackRef) {
        this.orderNotices.delete(orderId);
      }
      subscription.unsubscribe();
    }));
  }

  private resolveOrderNotification(rawOrderId: number) {
    const orderId = Number(rawOrderId);
    if (!orderId) return;
    this.rememberOrderId(this.resolvedOrderIds, orderId);
    this.stopOrderSound(orderId);
    const notice = this.orderNotices.get(orderId);
    if (!notice) return;
    this.orderNotices.delete(orderId);
    notice.subscription.unsubscribe();
    notice.snackRef.dismiss();
  }

  private stopOrderSound(orderId: number) {
    const audio = this.orderSounds.get(orderId);
    if (!audio) return;
    this.orderSounds.delete(orderId);
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
  }

  private rememberOrderId(ids: Set<number>, orderId: number) {
    ids.add(orderId);
    if (ids.size > 1000) ids.delete(ids.values().next().value);
  }

  async updateManagerUser(id: number) {
    if (!this.user) return;
    await this.claim(id).toPromise();
    return;
  }

  copyOrder(id: number) {
    return this.http.post<Order>(`${environment.BACKEND}/order/copy/${id}`, null);
  }

  cancelOrder(id: number, payload: CancelOrderDto = {}) {
    return this.http.put<Order>(`${environment.BACKEND}/order/${id}/cancel`, payload);
  }

  getCheck(id: number) {
    this.router.navigate([]).then(() => {
      window.open(`${environment.BACKEND}/order/check/${id}`, '_blank');
    });
  }

  getProductsInDoc(id: number) {
    return this.http.get<
      {
        id: number;
        order_product_id?: number;
        description: string;
        brand: string;
        article: string;
        quan: number;
        price: number;
        stock: number;
        provider: string;
        sum: number;
        shipped_quan: number;
        remaining_to_ship_quan: number;
        waiting_supply_quan: number;
        is_ready: boolean;
      }[]
    >(`${environment.BACKEND}/order/${id}/products`);
  }

  search(payload?: SearchOrderDto) {
    const normalizedPayload = normalizeDateRangePayload(payload || {});
    return this.http
      .post<SearchOrderResponseDto>(`${environment.BACKEND}/order/search`, normalizedPayload)
      .pipe(
        map((data) => {
          return {
            ...data,
            total: Number(data.total),
            limit: Number(data.limit),
            offset: Number(data.offset),
            data: data.data.map((obj) => {
              return {
                ...obj,
                sum: Number(obj.sum),
                invoice_total: Number(obj.invoice_total),
                paid_total: Number(obj.paid_total),
                returned_total: Number(obj.returned_total || 0),
                collectible_total: Number(obj.collectible_total || 0),
                refunded_total: Number(obj.refunded_total || 0),
                refund_due_total: Number(obj.refund_due_total || 0),
                has_returns: Boolean(obj.has_returns),
                date: new Date(obj.date),
                date_expected: new Date(obj.date_expected),
                date_canceled: obj.date_canceled ? new Date(obj.date_canceled) : null,
                status_summary: obj.status_summary
                  ? {
                      ...obj.status_summary,
                      invoice_total: Number(obj.status_summary.invoice_total || 0),
                      paid_total: Number(obj.status_summary.paid_total || 0),
                      returned_total: Number(obj.status_summary.returned_total || 0),
                      collectible_total: Number(obj.status_summary.collectible_total || 0),
                      refunded_total: Number(obj.status_summary.refunded_total || 0),
                      refund_due_total: Number(obj.status_summary.refund_due_total || 0),
                      has_returns: Boolean(obj.status_summary.has_returns)
                    }
                  : obj.status_summary
              };
            })
          };
        })
      );
  }

  getIncomingB2cOrders() {
    return this.http.get<SearchOrderResponseDto>(`${environment.BACKEND}/order/incoming-b2c`).pipe(
      map((data) => {
        return {
          ...data,
          total: Number(data.total),
          limit: Number(data.limit),
          offset: Number(data.offset),
          data: data.data.map((obj) => {
            return {
              ...obj,
              sum: Number(obj.sum),
              invoice_total: Number(obj.invoice_total),
              paid_total: Number(obj.paid_total),
              returned_total: Number(obj.returned_total || 0),
              collectible_total: Number(obj.collectible_total || 0),
              refunded_total: Number(obj.refunded_total || 0),
              refund_due_total: Number(obj.refund_due_total || 0),
              has_returns: Boolean(obj.has_returns),
              date: new Date(obj.date),
              date_expected: new Date(obj.date_expected),
              date_canceled: obj.date_canceled ? new Date(obj.date_canceled) : null,
              status_summary: obj.status_summary
                ? {
                    ...obj.status_summary,
                    invoice_total: Number(obj.status_summary.invoice_total || 0),
                    paid_total: Number(obj.status_summary.paid_total || 0),
                    returned_total: Number(obj.status_summary.returned_total || 0),
                    collectible_total: Number(obj.status_summary.collectible_total || 0),
                    refunded_total: Number(obj.status_summary.refunded_total || 0),
                    refund_due_total: Number(obj.status_summary.refund_due_total || 0),
                    has_returns: Boolean(obj.status_summary.has_returns)
                  }
                : obj.status_summary
            };
          })
        };
      })
    );
  }

  getOne(id: number) {
    return this.http.get<Order>(`${environment.BACKEND}/order/${id}`).pipe(
      map((order) => {
        order.date_created = new Date(order.date_created);
        order.date_expected = new Date(order.date_expected);
        order.date_canceled = order.date_canceled ? new Date(order.date_canceled) : null;
        if (order.status_summary) {
          order.status_summary = {
            ...order.status_summary,
            invoice_total: Number(order.status_summary.invoice_total || 0),
            paid_total: Number(order.status_summary.paid_total || 0),
            returned_total: Number(order.status_summary.returned_total || 0),
            collectible_total: Number(order.status_summary.collectible_total || 0),
            refunded_total: Number(order.status_summary.refunded_total || 0),
            refund_due_total: Number(order.status_summary.refund_due_total || 0),
            has_returns: Boolean(order.status_summary.has_returns)
          };
        }
        return order;
      })
    );
  }

  create(payload: CreateOrderDto) {
    return this.http.post<Order>(`${environment.BACKEND}/order/private_create`, payload);
  }

  delete(id: number) {
    return this.http.delete(`${environment.BACKEND}/order/${id}`);
  }

  save(payload: SaveOrderDto) {
    return this.http.put<Order>(`${environment.BACKEND}/order/save`, {
      ...payload
    });
  }

  changeWarehouse(payload: SaveOrderDto & {
    request_id: string;
    expected_warehouse_id: number;
    warehouse_id: number;
  }) {
    return this.http.post<Order>(`${environment.BACKEND}/order/${payload.id}/warehouse`, payload);
  }

  claim(id: number) {
    return this.http.post<Order>(`${environment.BACKEND}/order/${id}/claim`, null).pipe(
      tap(() => this.resolveOrderNotification(id))
    );
  }

  createPaymentFlow(id: number, payload: CreateOrderPaymentFlowDto) {
    return this.http.post<OrderFlowResult>(
      `${environment.BACKEND}/order/${id}/flow/payment`,
      payload
    );
  }

  createShipmentFlow(id: number, payload: CreateOrderShipmentFlowDto) {
    return this.http.post<OrderFlowResult>(
      `${environment.BACKEND}/order/${id}/flow/shipment`,
      payload
    );
  }

  createSaleFlow(id: number, payload: CreateOrderSaleFlowDto) {
    return this.http.post<OrderFlowResult>(`${environment.BACKEND}/order/${id}/flow/sale`, payload);
  }

  previewUnavailableProducts(id: number, payload: OrderUnavailableProductsPayload) {
    return this.http.post<OrderUnavailableProductsResult>(
      `${environment.BACKEND}/order/${id}/unavailable-products/preview`,
      payload
    );
  }

  applyUnavailableProducts(id: number, payload: OrderUnavailableProductsPayload) {
    return this.http.post<OrderUnavailableProductsResult>(
      `${environment.BACKEND}/order/${id}/unavailable-products/apply`,
      payload
    );
  }
}
